import React, { useCallback, useMemo } from 'react';
import { orderBy, sum } from 'lodash';

import BarChart from '../../../charts/BarChart';

import { formatChartPrice } from '../../../../utils/utils';

import colorPalette, { lighterColorPalette } from '../colorPalette';

export const CustomTooltip = (props) => {
  const { data, value, surgeons } = props;
  const id = data?.id?.includes('compare') ? data?.id?.substring(0, data?.id.indexOf('_compare')) : data?.id;
  const surgeon = surgeons?.find((u) => u.id === id);
  const name = `${surgeon.firstName} ${surgeon.lastName}`;

  return (
    <div className="custom-tooltip">
      <div className="tooltip-label">{surgeon ? (data?.id?.includes('compare') ? `${name} (previous period)` : name) : ''}</div>
      <div className="tooltip-content">
        <div>{`$${formatChartPrice(value, 0)}`}</div>
      </div>
    </div>
  );
};

const generateColors = (keys, isPrevious) => {
  const colors = {};
  keys?.forEach((k, index) => {
    colors[k] = isPrevious ? lighterColorPalette[index] : colorPalette[index];
  });
  return colors;
};

const SurgeonsProformaBarChart = ({ cases = [], surgeons = [] }) => {
  const data = useMemo(() => {
    const allSurgeons = surgeons
      .map((surgeon) => {
        const surgeonCases = cases.filter((item) => item.surgeon === surgeon.id);
        const value = sum(surgeonCases?.map((caseItem) => caseItem.proformaValue || 0));
        return {
          id: surgeon.id,
          label: surgeon.lastName,
          value,
        };
      })
      .filter((item) => item.value !== 0);

    const sorted = orderBy(allSurgeons, 'value', 'desc');
    const top10 = sorted.splice(0, 10);

    return top10;
  }, [cases, surgeons]);

  const getSurgeonName = useCallback((surgeonId) => {
    const id = surgeonId?.includes('compare') ? surgeonId?.substring(0, surgeonId.indexOf('_compare')) : surgeonId;
    const surgeon = surgeons?.find((u) => u.id === id);
    return surgeon ? (surgeonId?.includes('compare') ? `${surgeon.lastName} (vs)` : surgeon.lastName) : '';
  }, [surgeons]);

  const colors = useMemo(() => generateColors(data?.filter((item) => !item?.id?.includes('_compare'))?.map((item) => item.id)), [data]);
  const previousColors = useMemo(() => generateColors(data?.filter((item) => item?.id?.includes('_compare'))?.map((item) => item.id), true), [data]);

  return (
    <div className="management-reports-chart__container m-r-md" style={{ width: '50%' }}>
      <div className="font-size-bg font-bold">Top Surgeons by Total Value</div>
      <div className="secondary m-t-sm m-b-lg">Top 10 surgeons by proforma value</div>
      <BarChart
        data={data || []}
        indexBy="id"
        height="300px"
        layout="horizontal"
        enableLabel={true}
        margin={{ top: 10, right: 25, bottom: 30, left: 85 }}
        colors={(bar) => {
          if (bar.indexValue?.includes('_compare')) {
            return previousColors[bar.indexValue];
          }
          return colors[bar.indexValue];
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          truncateTickAt: 0,
          format: id => getSurgeonName(id),
        }}
        labelTextColor={{
          from: 'color',
          modifiers: [
            [
              'darker',
              1.6,
            ],
          ],
        }}
        label={(d) => `$${formatChartPrice(d.value, 0)}`}
        CustomTooltip={(props) => <CustomTooltip {...props} surgeons={surgeons} />}
      />
    </div>
  );
};

export default SurgeonsProformaBarChart;
